import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { ApplicationsBreadcrumb } from '../ApplicationsBreadcrumbs'
import { PageHeader } from '@/components/ui/PageHeader'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { useCurrentApplication } from '../useCurrentApplication'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from 'react'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import clsx from 'clsx'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MermaidRenderer } from '@/catalog-apps/components/component-library/MdTextComponent/MermaidRenderer'
import { Button } from '@/components/shadcn/button'
import { MDXEditorComponent } from '@/components/form-components/MDXEditorComponent'
import { ActionsMenu } from '@/catalog-apps/components/component-library/MdTextComponent/ActionsMenu/ActionsMenu'
import type { MdTextAction } from '@/catalog-apps/components/component-library/MdTextComponent/types'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { printMdTextContent } from '@/catalog-apps/components/component-library/MdTextComponent/printMdTextContent'
import { useMdTextSearch } from '@/catalog-apps/components/component-library/MdTextComponent/useMdTextSearch'
import { MdTextSearchNavigator } from '@/catalog-apps/components/component-library/MdTextComponent/MdTextSearchNavigator'
import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { useSessionStorage } from '@/hooks'
import { isApplicationAdmin, isSuperUser } from '@/util'
import { EmptyState } from '@/components/ui/EmptyState'
import { COMPONENT_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { getMarkdownContent } from '../AppTermsPage/useGetTerms'
import {
  useGetPrivacyPolicyMarkdown,
  useInvalidatePrivacyPolicyMarkdown,
  useSavePrivacyPolicyMarkdown,
} from './useGetPrivacyPolicy'

export function AppPrivacyPolicyPage() {
  return (
    <>
      <PageHeader className="pr-3" title={<ApplicationsBreadcrumb title="Privacy Policy" />} />

      <div className="flex items-center gap-20">
        <QueryErrorBoundary errorMessage="Failed to get privacy policy">
          <Suspense fallback={<LoadingComponent text="Getting privacy policy..." />}>
            <AppPrivacyPolicyPageContent />
          </Suspense>
        </QueryErrorBoundary>
      </div>
    </>
  )
}

function AppPrivacyPolicyPageContent() {
  const [searchValue, setSearchValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const { applicationId, appName } = useCurrentApplication()
  const { data } = useGetPrivacyPolicyMarkdown(applicationId)
  const { mutate: savePrivacyPolicy, isPending: isSavingPrivacyPolicy } = useSavePrivacyPolicyMarkdown(applicationId)
  const invalidatePrivacyPolicy = useInvalidatePrivacyPolicyMarkdown(applicationId)
  const [roles] = useSessionStorage('roles', 0)
  const canEdit = isSuperUser(roles) || isApplicationAdmin(roles)

  const privacyPolicyContent = getMarkdownContent(data)
  const isPrivacyPolicyEmpty = privacyPolicyContent.trim() === ''
  const [markdown, setMarkdown] = useState(privacyPolicyContent)
  const printableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMarkdown(privacyPolicyContent)
  }, [privacyPolicyContent])

  const privacyPolicyActions = useMemo<MdTextAction[]>(() => {
    if (!canEdit) {
      return []
    }

    return [
      {
        id: 'edit',
        label: 'Edit',
        icon: 'IconEditOutline',
        visible: true,
        confirmationRequired: false,
        handlerId: '',
        type: 'edit',
      },
    ]
  }, [canEdit])

  const onCancelEditing = useCallback(() => {
    setIsEditing(false)
    setMarkdown(privacyPolicyContent)
  }, [privacyPolicyContent])

  const openEditing = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleSubmit = useCallback(() => {
    savePrivacyPolicy(
      { content: markdown },
      {
        onSuccess: () => {
          setIsEditing(false)
          void invalidatePrivacyPolicy()
        },
      }
    )
  }, [invalidatePrivacyPolicy, markdown, savePrivacyPolicy])

  const handlePrintToPdf = useCallback(() => {
    printMdTextContent(printableRef.current)
  }, [])

  const reloadHandler = useCallback(() => {
    void invalidatePrivacyPolicy()
  }, [invalidatePrivacyPolicy])

  const handleDiffWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    const target = event.target

    if (!(target instanceof HTMLElement)) {
      return
    }

    const diffEditor = target.closest('.mdxeditor-diff-editor')

    if (!diffEditor) {
      return
    }

    const scrollers = Array.from(diffEditor.querySelectorAll<HTMLElement>('.cm-scroller')).filter(
      (scroller) => scroller.scrollHeight > scroller.clientHeight || scroller.scrollWidth > scroller.clientWidth
    )

    if (scrollers.length === 0) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    scrollers.forEach((scroller) => {
      scroller.scrollTop += event.deltaY
      scroller.scrollLeft += event.deltaX
    })
  }, [])

  const { contentRef, hasSearchTerm, matchCount, activeMatchIndex, goToPreviousMatch, goToNextMatch } = useMdTextSearch(
    {
      searchValue,
      isEditing,
      content: privacyPolicyContent,
      highlightSectionBody: privacyPolicyContent,
    }
  )

  return (
    <TablePageContainer className="flex min-h-0 w-full flex-col overflow-hidden">
      <div ref={printableRef} className="md-text-component flex min-h-0 flex-1 flex-col overflow-hidden">
        <ComponentHeader className="flex-shrink-0 pt-5" wrapperClassName="border-b-0">
          <ComponentTitle title={`${appName} - Privacy Policy`} />

          <ComponentActionsContainer className="md-text-component__actions">
            {!isEditing && !isPrivacyPolicyEmpty && (
              <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search" />
            )}

            {!isEditing && !isPrivacyPolicyEmpty && (
              <ActionsMenu
                actions={privacyPolicyActions}
                openEditing={openEditing}
                markdown={privacyPolicyContent}
                reloadHandler={reloadHandler}
                onExportPdf={handlePrintToPdf}
              />
            )}

            {isEditing && (
              <>
                <Button variant="ghost" size="sm" onClick={onCancelEditing}>
                  <IconCloseOutline size={12} />
                  <span className="ml-2">Cancel editing</span>
                </Button>
                <Button size="sm" onClick={handleSubmit} disabled={isSavingPrivacyPolicy}>
                  <span>{isSavingPrivacyPolicy ? 'Saving...' : 'Save'}</span>
                </Button>
              </>
            )}
          </ComponentActionsContainer>
        </ComponentHeader>

        {isEditing ? (
          <div className="flex min-h-0 flex-1 overflow-hidden px-1 pb-4" onWheelCapture={handleDiffWheel}>
            <MDXEditorComponent
              markdown={markdown}
              onChange={setMarkdown}
              contentEditableClassName="legal-markdown h-auto min-h-full bg-secondary/5"
              className="flex h-full min-h-0 flex-1 flex-col overflow-hidden overscroll-contain [&_.cm-editor]:!h-full [&_.cm-editor]:!min-h-0 [&_.cm-editor]:bg-secondary/5 [&_.cm-scroller]:overflow-y-auto [&_.cm-scroller]:overscroll-contain [&_.mdxeditor-diff-editor]:min-h-0 [&_.mdxeditor-diff-editor]:flex-1 [&_.mdxeditor-diff-editor]:overflow-hidden [&_.mdxeditor-diff-editor_.cm-mergeView]:h-full [&_.mdxeditor-diff-source-wrapper]:flex [&_.mdxeditor-diff-source-wrapper]:min-h-0 [&_.mdxeditor-diff-source-wrapper]:flex-1 [&_.mdxeditor-diff-source-wrapper]:flex-col [&_.mdxeditor-diff-source-wrapper]:overflow-hidden [&_.mdxeditor-rich-text-editor]:min-h-0 [&_.mdxeditor-rich-text-editor]:flex-1 [&_.mdxeditor-rich-text-editor]:overflow-hidden [&_.mdxeditor-root-contenteditable]:h-full [&_.mdxeditor-root-contenteditable]:min-h-0 [&_.mdxeditor-root-contenteditable]:overflow-y-auto [&_.mdxeditor-root-contenteditable]:overscroll-contain [&_.mdxeditor-source-editor]:min-h-0 [&_.mdxeditor-source-editor]:flex-1"
              autoFocus={true}
              diffMarkdown={privacyPolicyContent}
            />
          </div>
        ) : isPrivacyPolicyEmpty ? (
          <EmptyState
            className="min-h-0 flex-1"
            style={{ transform: `translateY(-${COMPONENT_HEADER_HEIGHT}px)` }}
            title="No privacy policy content yet"
            description={
              canEdit
                ? "Add markdown for this application's privacy policy. You can use headings, lists, tables, and diagrams."
                : 'A privacy policy has not been published for this application yet.'
            }
            actions={
              canEdit ? (
                <Button size="sm" onClick={openEditing}>
                  Add content
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="relative min-h-0 flex-1">
            <MdTextSearchNavigator
              hasSearchTerm={hasSearchTerm}
              matchCount={matchCount}
              activeMatchIndex={activeMatchIndex}
              onPrevious={goToPreviousMatch}
              onNext={goToNextMatch}
            />

            <ScrollArea
              className={clsx('md-text-component__scroll-area h-full min-h-0')}
              viewportClassName="md-text-component__viewport"
            >
              <div
                ref={contentRef}
                className="md-text-component__content legal-markdown prose prose-sm max-w-full gap-10 px-1 pb-8 pt-5 dark:prose-invert"
              >
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    pre({ children, ...props }) {
                      const codeElement = children as React.ReactElement

                      if (codeElement?.props?.className?.includes('language-mermaid')) {
                        return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
                      }

                      return <pre {...props}>{children}</pre>
                    },

                    code({ className, children, ...props }) {
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      )
                    },
                  }}
                >
                  {privacyPolicyContent}
                </Markdown>
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </TablePageContainer>
  )
}

